import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { PlayerMe, SseEventType } from "@redchain/contracts";
import { api } from "../api/client";
import { sseBus } from "../api/events";
import { useWallet } from "../wallet/WalletProvider";

interface PlayerContextValue {
  me: PlayerMe | null;
  loading: boolean;
  error: string | null;
  /** Synchronous last error from the most recent `refresh()` (avoids stale React state). */
  lastError(): string | null;
  refresh(): Promise<PlayerMe | null>;
  /** True while /play has a running session (items claims are disabled, §15.5.4). */
  inSession: boolean;
  setInSession(v: boolean): void;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

const REFRESH_ON: SseEventType[] = [
  "chain.committed",
  "money.changed",
  "vault.changed",
  "vault.funded",
  "items.changed",
  "rcd.claimed",
  "rcd.deposited",
  "rcd.deposit_pending",
  "rcd.refunded",
  "item.claimed",
  "item.deposited",
  "sync.pending_cleared",
  "mon.refunded",
];

export function PlayerProvider({ children }: { children: ReactNode }) {
  const { auth } = useWallet();
  const [me, setMe] = useState<PlayerMe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inSession, setInSession] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorRef = useRef<string | null>(null);
  const lastError = useCallback(() => errorRef.current, []);

  const refresh = useCallback(async () => {
    if (!api.token) {
      setMe(null);
      return null;
    }
    setLoading(true);
    try {
      const m = await api.me();
      setMe(m);
      errorRef.current = null;
      setError(null);
      return m;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      errorRef.current = msg;
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth) void refresh();
    else setMe(null);
  }, [auth, refresh]);

  useEffect(
    () =>
      sseBus.onAny((ev) => {
        if (!REFRESH_ON.includes(ev.type)) return;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => void refresh(), 500);
      }),
    [refresh],
  );

  return (
    <PlayerContext.Provider value={{ me, loading, error, lastError, refresh, inSession, setInSession }}>{children}</PlayerContext.Provider>
  );
}

export function usePlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer outside PlayerProvider");
  return ctx;
}
